import { Button } from '../ui/button';
import { apiUrl } from '../../lib/api';

/**
 * SSO is enabled per-domain via the build-time flag `VITE_SSO_ENABLED`
 * (set once the OAuth backend is configured). Until then the buttons render in
 * their final position but disabled, so the layout never shifts when they light
 * up.
 */
const SSO_ENABLED = import.meta.env.VITE_SSO_ENABLED === 'true';

const PROVIDERS = [
  { id: 'google', label: 'Google', icon: <GoogleIcon /> },
  { id: 'github', label: 'GitHub', icon: <GitHubIcon /> },
] as const;

/** Starts the server-side OAuth flow with a full-page navigation — the API sets
 * the session cookie and redirects back to /auth/callback. */
function startOAuth(provider: string): void {
  window.location.href = apiUrl(`/auth/oauth/${provider}`);
}

/** Google / GitHub sign-in buttons, shared by the login and register pages. */
export function SsoButtons() {
  return (
    <div className="space-y-2">
      {PROVIDERS.map((p) => (
        <Button
          key={p.id}
          type="button"
          variant="outline"
          disabled={!SSO_ENABLED}
          onClick={SSO_ENABLED ? () => startOAuth(p.id) : undefined}
          title={SSO_ENABLED ? undefined : 'Single sign-on is coming soon'}
          className="w-full justify-between"
        >
          <span className="flex items-center gap-2">
            {p.icon}
            Continue with {p.label}
          </span>
          {!SSO_ENABLED && (
            <span className="rounded-sm bg-surface-3 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted">
              Soon
            </span>
          )}
        </Button>
      ))}
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.02-3.7H.96v2.34A9 9 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.98 10.72a5.4 5.4 0 0 1 0-3.44V4.94H.96a9 9 0 0 0 0 8.12l3.02-2.34z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58C13.47.9 11.43 0 9 0A9 9 0 0 0 .96 4.94l3.02 2.34C4.68 5.16 6.66 3.58 9 3.58z"
      />
    </svg>
  );
}

function GitHubIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82a7.6 7.6 0 0 1 4 0c1.53-1.03 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.01 8.01 0 0 0 16 8c0-4.42-3.58-8-8-8z" />
    </svg>
  );
}
